type SeaiDwellingRecord = Record<string, unknown>;
type FuelCode = number | null;
type FuelCodeCandidate = [code: unknown, include?: boolean];

const FOSSIL_FUEL_CODES = new Set([2, 3, 4, 5]);

export function hasNoFossilHeatingFuelCodes(
  dwelling?: string | object | null
): boolean {
  const dwellingRecord = parseDwelling(dwelling);
  if (!dwellingRecord) return false;

  const fuelCodes = getRelevantHeatingFuelCodes(dwellingRecord);
  return fuelCodes.length > 0
    && fuelCodes.every((code) => code !== null && !FOSSIL_FUEL_CODES.has(code));
}

function parseDwelling(dwelling?: string | object | null): SeaiDwellingRecord | null {
  if (!dwelling) return null;
  if (typeof dwelling === "object") return asRecord(dwelling) ?? null;

  try {
    return asRecord(JSON.parse(dwelling)) ?? null;
  } catch {
    return null;
  }
}

function getRelevantHeatingFuelCodes(dwelling: SeaiDwellingRecord): FuelCode[] {
  const heatingScheme = readRecord(dwelling, "HeatingSchemeDetails");
  const deliveredEnergy = readRecord(readRecord(dwelling, "ResultsDetails"), "DeliveredEnergyDetails");
  const individualScheme = readRecord(heatingScheme, "IndividualSchemeDetails");
  const groupScheme = readRecord(heatingScheme, "GroupSchemeDetails");

  if (individualScheme) return getIndividualSchemeFuelCodes(individualScheme, deliveredEnergy);
  return groupScheme ? getGroupSchemeFuelCodes(groupScheme, deliveredEnergy) : [];
}

function getIndividualSchemeFuelCodes(
  scheme: SeaiDwellingRecord,
  deliveredEnergy?: SeaiDwellingRecord
): FuelCode[] {
  return collectFuelCodes([
    [scheme.MainSHFuel],
    [scheme.MainWHFuel],
    [scheme.MainSH2Fuel, isPositive(scheme.HSMainSystem2Efficiency)],
    [scheme.SupplSHFuel, isPositive(scheme.HSSupplHeatFraction) || isPositive(deliveredEnergy?.SHSec)],
    [scheme.SupplWHFuel, isPositive(deliveredEnergy?.WHSec)],
    [scheme.CHPFuelTypeID, isPositive(scheme.CHPMainHeatFraction) || isPositive(deliveredEnergy?.CHPInput)],
  ]);
}

function getGroupSchemeFuelCodes(
  scheme: SeaiDwellingRecord,
  deliveredEnergy?: SeaiDwellingRecord
): FuelCode[] {
  return [
    ...getActiveBoilerFuelCodes(scheme),
    ...collectFuelCodes([
      [scheme.MainWHFuel],
      [scheme.SupplHSFuelTypeID, isPositive(scheme.HSSupplHeatFraction) || isPositive(deliveredEnergy?.SHSec)],
      [scheme.CHPFuelType, isPositive(scheme.CHPUnitHeatFraction) || isPositive(deliveredEnergy?.CHPInput)],
    ]),
  ];
}

function getActiveBoilerFuelCodes(scheme: SeaiDwellingRecord): FuelCode[] {
  return toArray(scheme.Boilers)
    .map(asRecord)
    .filter((boiler): boiler is SeaiDwellingRecord => !!boiler && isPositive(boiler.PercentageHeat))
    .map((boiler) => toFuelCode(boiler.BoilerFuelType));
}

function collectFuelCodes(candidates: FuelCodeCandidate[]): FuelCode[] {
  return candidates.flatMap(([code, include = true]) => include ? [toFuelCode(code)] : []);
}

function toFuelCode(rawCode: unknown): FuelCode {
  const code = Number(rawCode);
  return Number.isFinite(code) && code > 0 ? code : null;
}

function readRecord(record: SeaiDwellingRecord | undefined, key: string): SeaiDwellingRecord | undefined {
  return asRecord(record?.[key]);
}

function asRecord(value: unknown): SeaiDwellingRecord | undefined {
  return value && typeof value === "object" ? value as SeaiDwellingRecord : undefined;
}

function toArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
}

function isPositive(value: unknown): boolean {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0;
}
